import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  CalendarArrowLeft,
  CalendarArrowRight,
  Comment,
  Delete,
  Files,
  Paperclip,
  People,
  Replace,
  Suitcase,
} from '@factorialco/f0-react/icons/app'
import {
  BulkUpdateTasksDocument,
  ProjectTasksDataCollectionDocument,
  ProjectTasksDataCollectionItemFragment,
  UpdateProjectTaskDocument,
} from 'generated/resources/graphql'
import { useWindowSize } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  getStatusColor,
  getStatusLabel,
} from 'modules/project_management/components/labelTags/TaskStatusTag'
import { isValidTaskStatus, taskStatuses } from 'modules/tasks/lib/status'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { DeleteModal } from './modals/DeleteModal'
import { DuplicateModal } from './modals/DuplicateModal'

const MODAL: {
  [key in Exclude<BulkActionType, 'ChangeStatus'>]: React.ComponentType<{
    onCloseModal: () => void
    ids: number[]
    projectName: string
  }>
} = {
  ['Duplicate']: DuplicateModal,
  ['Delete']: DeleteModal,
}

export type BulkActionType = 'ChangeStatus' | 'Delete' | 'Duplicate'

interface ProjectTasksDataCollectionProps {
  projectId: number
  projectName: string
  canManageTasks: boolean
  onClickCreateTask: (newStatus?: string) => void
}

const ProjectTasksDataCollection = ({
  projectId,
  projectName,
  canManageTasks,
  onClickCreateTask,
}: ProjectTasksDataCollectionProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [actionType, setActionType] = useState<Exclude<BulkActionType, 'ChangeStatus'> | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const ActionModal = useMemo(() => (actionType ? MODAL[actionType] : null), [actionType])
  const onCloseModal = () => setActionType(null)

  const containerRef = useRef<HTMLDivElement>(null)

  const [availableHeight, setAvailableHeight] = useState<number>()

  const size = useWindowSize()

  useEffect(() => {
    setAvailableHeight(containerRef.current?.parentElement?.parentElement?.offsetHeight)
  }, [size.height])

  const [updateTask] = useMutation(UpdateProjectTaskDocument, {
    extractorFn: (mutationData) =>
      mutationData.projectManagementMutations.updateProjectmanagementProjectTask,
    ignoreResults: true,
    awaitRefetchQueries: false,
    onQueryUpdated: () => false,
  })

  const [bulkUpdateTasks] = useMutation(BulkUpdateTasksDocument, {
    extractorFn: (data) => data.tasksMutations.bulkUpdateTasksTask,
    update: (cache) => {
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'projectManagement',
        broadcast: false,
      })
      cache.gc()
    },
    awaitRefetchQueries: true,
    refetchQueries: ['ProjectTasksDataCollection'],
  })

  const fromKanban = false

  const handleResponse = useUnsafeCallback<
    (
      result: UnhandledResult<Result<unknown>>,
      source: ProjectTasksDataCollectionItemFragment
    ) => ProjectTasksDataCollectionItemFragment
  >((result, source) => {
    const handled = handleResult(result, (parsedErrors) => {
      app.setFlash({
        type: 'wrong',
        message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      return source
    }
    throw Error('')
  }, [])

  const handleMove = useUnsafeCallback(
    async (
      _fromLaneId: string,
      toLaneId: string,
      sourceRecord: ProjectTasksDataCollectionItemFragment,
      destinationRecord: {
        record: ProjectTasksDataCollectionItemFragment
        position: 'above' | 'below'
      } | null
    ): Promise<ProjectTasksDataCollectionItemFragment> => {
      const status = toLaneId
      const task = sourceRecord.task
      if (!isValidTaskStatus(status) || !task) return sourceRecord

      const result = await updateTask({
        variables: {
          id: sourceRecord.id,
          status,
          name: task.name,
          projectId: projectId,
          beforePosition:
            destinationRecord?.position === 'below'
              ? destinationRecord?.record?.position
              : undefined,
          afterPosition:
            destinationRecord?.position === 'above'
              ? destinationRecord?.record?.position
              : undefined,
          subprojectId: sourceRecord.subproject?.id,
          dueOn: task.dueOn,
          startsOn: task.startsOn,
        },
      })

      track(trackingEvents.projectManagement.tasks.updateTaskStatus, {
        mark_as: `Mark as ${status}`,
        number: 1,
        identifier: 'project',
        path_page: 'project tasks',
        origin: 'kanban',
      })

      return handleResponse(result, sourceRecord)
    },
    [handleResponse, updateTask, projectId]
  )

  const handleCreate = useUnsafeCallback(async (newStatus: string) => {
    onClickCreateTask(newStatus)
  }, [])

  const source = useGraphqlDataCollectionSource({
    lanes: taskStatuses().map((status) => ({
      id: status.status,
      filters: { status: status.status },
    })),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.tasks.total_items', { count: totals })
    },
    document: ProjectTasksDataCollectionDocument,
    mapData: (data) => data.projectManagement.projectTasksConnection.nodes,
    paginationType: 'infinite-scroll',
    mapVariables: ({ search, sortings, filters }) => {
      const status = filters.status + ''
      return {
        companyId: companyId,
        projectIds: [projectId],
        search,
        filter: filters?.status ? { status: { eq: status } } : undefined,
        sortOrder: sortings?.[0] ?? { field: 'position', order: 'asc' },
      }
    },
    mapPagination: (data) => {
      return {
        total: data.projectManagement.projectTasksConnection.totalCount ?? 0,
        cursor: data.projectManagement.projectTasksConnection.pageInfo.endCursor ?? '0',
        hasNextPage: data.projectManagement.projectTasksConnection.pageInfo.hasNextPage ?? false,
      }
    },
    itemUrl: (item: ProjectTasksDataCollectionItemFragment) =>
      `${navigation.tasks.taskIdAndIdentifier.fullPath({
        taskId: Number(item.task?.id),
        identifier: TaskIdentifier.Project,
      })}?view=${fromKanban ? 'kanban' : 'table'}`,
    primaryActions: canManageTasks
      ? () => {
          return {
            label: i18n.t('project_management.tasks.add_button'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.projectManagement.tasks.createProjectTaskButton, {
                origin: fromKanban ? 'kanban' : 'datatable',
              })
              onClickCreateTask()
            },
          }
        }
      : undefined,
    selectable: (item: ProjectTasksDataCollectionItemFragment) => item.task?.id,
    bulkActions: canManageTasks
      ? ({ selectedCount }: { selectedCount: number }) => {
          return {
            primary: [
              {
                label: i18n.t('tasks.status.change_status'),
                id: 'change-status',
                icon: Replace,
                loading: bulkUpdateTasks,
              },
              {
                label: i18n.t('tasks.status.todo'),
                id: 'change-status;todo',
              },
              {
                label: i18n.t('tasks.status.done'),
                id: 'change-status;done',
              },
              {
                label: i18n.t('tasks.status.in_progress'),
                id: 'change-status;in_progress',
              },
              {
                label: i18n.t('tasks.status.discarded'),
                id: 'change-status;discarded',
              },
            ],
            secondary: [
              {
                label: i18n.t('common.delete'),
                icon: Delete,
                critical: true,
                id: 'delete',
              },
              {
                label: i18n.t('common.duplicate'),
                icon: Files,
                id: 'duplicate',
              },
              ...(selectedCount === 1
                ? [
                    {
                      label: i18n.t('common.edit'),
                      id: 'edit',
                    },
                  ]
                : []),
            ],
          }
        }
      : undefined,
    search: {
      enabled: true,
    },
  })

  return (
    <Box
      ref={containerRef}
      style={availableHeight ? { height: `${availableHeight - 1}px` } : undefined}
    >
      <OneDataCollection
        source={source}
        fullHeight
        onBulkAction={async (action, selectedItems) => {
          const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)

          setSelectedIds(selectedCheckedItems.map((item) => item.item.id))

          if (action.startsWith('change-status')) {
            const [, status] = action.split(';')

            if (status && isValidTaskStatus(status)) {
              const result = await bulkUpdateTasks({
                variables: {
                  tasks: selectedCheckedItems.map((item) => ({
                    id: item.item.task?.id ?? 0,
                    status,
                    identifier: TaskIdentifier.Project,
                  })),
                },
              })

              const handled = handleResult(result, (_) => {})
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('common.saved_successfully'),
                })
              }
            }
          }

          switch (action) {
            case 'delete':
              setActionType('Delete')
              break
            case 'duplicate':
              setActionType('Duplicate')
              break
            case 'edit':
              const [taskId] = selectedCheckedItems.map((item) => item.item.id)

              navigate(
                navigation.projects.id.details.tasks.taskId.fullPath({
                  id: projectId,
                  taskId: Number(taskId),
                })
              )
              break
          }
        }}
        visualizations={[
          {
            type: 'kanban',
            options: {
              lanes: taskStatuses().map((status) => ({
                id: status.status,
                title: getStatusLabel(status.status),
                variant: getStatusColor(status.status),
              })),
              onMove: handleMove,
              onCreate: handleCreate,
              title: (projectTask: ProjectTasksDataCollectionItemFragment) =>
                projectTask.task?.name ?? '',
              metadata: (projectTask: ProjectTasksDataCollectionItemFragment) =>
                [
                  projectTask.subproject?.name
                    ? {
                        icon: Suitcase,
                        property: {
                          type: 'text' as const,
                          value: projectTask.subproject?.name ?? '',
                        },
                        tooltip: i18n.t('project_management.table.tasks.headers.subproject'),
                      }
                    : null,
                  projectTask.task?.startsOn
                    ? {
                        icon: CalendarArrowRight,
                        property: {
                          type: 'text' as const,
                          value: projectTask.task?.startsOn
                            ? convertDate(projectTask.task.startsOn, 'backend', 'ui_dmy_long')
                            : '',
                        },
                        tooltip: i18n.t('project_management.table.tasks.headers.starts_on'),
                      }
                    : null,

                  projectTask.task?.dueOn
                    ? {
                        icon: CalendarArrowLeft,
                        property: {
                          type: 'text' as const,
                          value: projectTask.task?.dueOn
                            ? convertDate(projectTask.task.dueOn, 'backend', 'ui_dmy_long')
                            : '',
                        },
                        tooltip: i18n.t('project_management.table.tasks.headers.due_on'),
                      }
                    : null,
                  {
                    icon: People,
                    property: {
                      type: 'avatarList' as const,
                      value: {
                        avatarList: projectTask.task?.assigneesConnection.nodes.map((node) => ({
                          type: 'person' as const,
                          firstName: node.firstName,
                          lastName: node.lastName,
                          src: node.avatar ? buildUrl(node.avatar) : undefined,
                        })),
                        max: 3,
                      },
                    },
                    tooltip: i18n.t('project_management.table.tasks.headers.assignees'),
                  },

                  projectTask.task?.commentsConnection.totalCount
                    ? {
                        icon: Comment,
                        property: {
                          type: 'number' as const,
                          value: projectTask.task?.commentsConnection.totalCount,
                        },
                        tooltip: i18n.t('project_management.export.comments'),
                      }
                    : null,

                  projectTask.task?.attachmentsConnection.totalCount
                    ? {
                        icon: Paperclip,
                        property: {
                          type: 'number' as const,
                          value: projectTask.task?.attachmentsConnection.totalCount,
                        },
                        tooltip: i18n.t('finance.sale_invoices.detail.documents'),
                      }
                    : null,
                ].filter(Boolean),
            },
          },
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('project_management.table.tasks.headers.name'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.task?.name,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.subproject'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.subproject?.name,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.assignees'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'avatarList',
                    value: {
                      avatarList:
                        projectTask.task?.assigneesConnection.nodes.map((node) => ({
                          type: 'person',
                          firstName: node.firstName,
                          lastName: node.lastName,
                          src: node.avatar ? buildUrl(node.avatar) : undefined,
                        })) ?? [],
                      max: 5,
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.status'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      label: projectTask.task?.status
                        ? getStatusLabel(projectTask.task?.status)
                        : '-',
                      status: projectTask.task?.status
                        ? getStatusColor(projectTask.task?.status)
                        : 'neutral',
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.starts_on'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.startsOn
                      ? new Date(projectTask.task?.startsOn)
                      : undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.due_on'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.dueOn ? new Date(projectTask.task?.dueOn) : undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.completed_at'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.completedAt
                      ? new Date(projectTask.task?.completedAt)
                      : undefined,
                  }),
                },
              ],
            },
          },
        ]}
      />
      {ActionModal && (
        <ActionModal onCloseModal={onCloseModal} ids={selectedIds} projectName={projectName} />
      )}
    </Box>
  )
}

export default ProjectTasksDataCollection
